import type { MutableValue } from '@amazon/vinyl-observable'
import { createTaskQueue, logDebug, TimeoutSlot } from '@amazon/vinyl-util'
import type { LoopMode } from '../core/cloudQueueOptions'
import type { PlaylistController, RepeatMode } from '../core/PlaylistController'
import { ctx } from '../env/playbackContext'
import { getPlaybackEnv } from '../env/playbackEnvRef'
import { setCloudQueueLoopMode, setCloudQueueShuffleMode } from './cloudQueueService'

const { target } = ctx('cloudQueueModeWriter')

const modeWriteDebounceSeconds = 0.3

const loopModeByRepeatMode: Readonly<Record<RepeatMode, LoopMode>> = {
    off: 'LOOP_OFF',
    all: 'LOOP_ALL',
    one: 'LOOP_ONE',
}

export interface CloudQueueModeWriterDeps {
    readonly playlistController: Pick<
        PlaylistController,
        | 'shuffleEnabled'
        | 'repeatMode'
        | 'cloudQueueOptions'
        | 'current'
        | 'currentCapabilities$'
        | 'feedAnchor'
    >
    readonly refetchQueue: () => Promise<void>
}

export interface CloudQueueModeWriter {
    /**
     * Applies the mode locally at once and settles it on the cloud queue. The promise settles with
     * the remote write that carries this press; a press superseded by a later one resolves.
     */
    setShuffleMode(enabled: boolean): Promise<void>
    setRepeatMode(mode: RepeatMode): Promise<void>
    toggleShuffleMode(): Promise<void>
    /** Steps off → all → one → off, skipping one when the current entry doesn't allow it. */
    cycleRepeatMode(): Promise<void>
    /**
     * Writes the presses made while `eagerStartId`'s queue was being created onto that queue,
     * now live; a press awaiting any other queue settles unwritten.
     */
    settlePressesAwaitingQueue(eagerStartId: string | null): void
}

const repeatCycle: readonly RepeatMode[] = ['off', 'all', 'one']

export function nextRepeatMode(mode: RepeatMode, loopOneAllowed: boolean): RepeatMode {
    const next = repeatCycle[(repeatCycle.indexOf(mode) + 1) % repeatCycle.length] ?? 'off'
    return next === 'one' && !loopOneAllowed ? 'off' : next
}

interface RemoteMode<T> {
    readonly queueId: string
    readonly value: T
    readonly confirmed: boolean
}

interface PendingPress<T> {
    readonly value: T
    readonly queueId: string
    readonly resolve: () => void
    readonly reject: (error: unknown) => void
}

interface PressAwaitingQueue<T> {
    readonly eagerStartId: string
    readonly value: T
    readonly previous: T
    readonly resolve: () => void
    readonly reject: (error: unknown) => void
}

interface ModeChannel<T> {
    readonly press: (value: T) => Promise<void>
    readonly settleAwaitingQueue: (eagerStartId: string | null) => void
}

/**
 * Each mode mutation drags a queue-window refetch behind it, so rapid presses must not fan out
 * into concurrent writes that land out of order and trip CloudQueue's rate limit.
 */
export function createCloudQueueModeWriter(deps: CloudQueueModeWriterDeps): CloudQueueModeWriter {
    const { playlistController, refetchQueue } = deps
    // Shuffle and loop share one chain because both rewrite the same queue window.
    const writes = createTaskQueue()
    const liveQueueId = () => playlistController.cloudQueueOptions.value?.queueId ?? null

    function createModeChannel<T>(
        name: string,
        local: MutableValue<T>,
        writeRemote: (queueId: string, value: T) => Promise<void>,
        noticeOnWriteFailure: boolean
    ): ModeChannel<T> {
        const timeout = new TimeoutSlot()
        let remote: RemoteMode<T> | null = null
        let pending: PendingPress<T> | null = null
        let awaitingQueue: PressAwaitingQueue<T> | null = null

        async function write(value: T, queueId: string): Promise<void> {
            // A settling write can outlive the queue it was pressed for.
            if (liveQueueId() !== queueId) {
                logDebug(target, `Dropping a ${name} write for a replaced queue`, { queueId })
                return
            }
            const known = remote
            if (!known || (known.confirmed && known.value === value)) return
            const restoreLocal = () => {
                // A later press owns local state unless it still shows what this write carried.
                if (local.value === value) local.value = known.value
            }
            try {
                await writeRemote(queueId, value)
            } catch (error) {
                if (remote === known) remote = { ...known, confirmed: false }
                restoreLocal()
                if (noticeOnWriteFailure)
                    getPlaybackEnv().notices.emit({ code: 'queueUpdateFailed' })
                throw error
            }
            try {
                await refetchQueue()
                if (remote === known) remote = { queueId, value, confirmed: true }
            } catch (error) {
                restoreLocal()
                // The mutation landed, so a failed write-back leaves the queue on the new mode.
                const writtenBack = await writeRemote(queueId, known.value).then(
                    () => true,
                    (revertError: unknown) => {
                        logDebug(target, `Failed to write the old ${name} mode back`, revertError)
                        return false
                    }
                )
                if (remote === known) {
                    remote = writtenBack
                        ? { ...known, confirmed: true }
                        : { queueId, value, confirmed: true }
                }
                throw error
            }
        }

        const enqueue = (value: T, queueId: string) => writes.enqueue(() => write(value, queueId))

        const flushTrailing = () => {
            if (!pending) return
            const { value, queueId, resolve, reject } = pending
            pending = null
            enqueue(value, queueId).then(resolve, reject)
        }

        function send(value: T, previous: T, queueId: string): Promise<void> {
            if (remote?.queueId !== queueId) remote = { queueId, value: previous, confirmed: false }

            const leading = !timeout.active
            timeout.set(flushTrailing, modeWriteDebounceSeconds)
            if (leading) return enqueue(value, queueId)

            pending?.resolve()
            return new Promise<void>((resolve, reject) => {
                pending = { value, queueId, resolve, reject }
            })
        }

        // An eager start plays before its queue exists, so its presses wait for the queue id.
        function awaitQueue(value: T, previous: T): Promise<void> {
            const eagerStartId = playlistController.current.value?.eagerStartId
            if (!eagerStartId) return Promise.resolve()
            const superseded = awaitingQueue
            awaitingQueue = null
            superseded?.resolve()
            return new Promise<void>((resolve, reject) => {
                awaitingQueue = {
                    eagerStartId,
                    value,
                    previous:
                        superseded?.eagerStartId === eagerStartId ? superseded.previous : previous,
                    resolve,
                    reject,
                }
            })
        }

        return {
            press: (value) => {
                const previous = local.value
                local.value = value
                const queueId = liveQueueId()
                return queueId ? send(value, previous, queueId) : awaitQueue(value, previous)
            },
            settleAwaitingQueue: (eagerStartId) => {
                const awaiting = awaitingQueue
                awaitingQueue = null
                if (!awaiting) return
                const queueId = liveQueueId()
                if (awaiting.eagerStartId !== eagerStartId || !queueId) {
                    awaiting.resolve()
                    return
                }
                local.value = awaiting.value
                send(awaiting.value, awaiting.previous, queueId).then(
                    awaiting.resolve,
                    awaiting.reject
                )
            },
        }
    }

    const currentEntityReferenceId = () => playlistController.feedAnchor()?.entityReferenceId

    const shuffle = createModeChannel(
        'shuffle',
        playlistController.shuffleEnabled,
        (queueId, enabled) =>
            setCloudQueueShuffleMode(
                queueId,
                enabled ? 'SHUFFLE_ON' : 'SHUFFLE_OFF',
                currentEntityReferenceId()
            ),
        false
    )

    const repeat = createModeChannel(
        'loop',
        playlistController.repeatMode,
        (queueId, mode: RepeatMode) =>
            setCloudQueueLoopMode(queueId, loopModeByRepeatMode[mode], currentEntityReferenceId()),
        true
    )

    return {
        setShuffleMode: shuffle.press,
        setRepeatMode: repeat.press,
        toggleShuffleMode: () => shuffle.press(!playlistController.shuffleEnabled.value),
        cycleRepeatMode: () =>
            repeat.press(
                nextRepeatMode(
                    playlistController.repeatMode.value,
                    playlistController.currentCapabilities$.value.loopOneAllowed
                )
            ),
        settlePressesAwaitingQueue: (eagerStartId) => {
            shuffle.settleAwaitingQueue(eagerStartId)
            repeat.settleAwaitingQueue(eagerStartId)
        },
    }
}
